import { MouseEvent, useRef, useState } from "react"

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { format, isSameDay, parse } from "date-fns"
import {
  ActiveModifiers,
  Button,
  ClassNames,
  DayOfWeek,
  DayPicker,
  DayPickerDefaultProps,
  DayPickerMultipleProps,
  DayPickerRangeProps,
  DayPickerSingleProps,
  DayProps,
  Matcher,
  SelectSingleEventHandler,
  useDayRender,
} from "react-day-picker"
import styles from "react-day-picker/dist/style.module.css"
import { twMerge } from "tailwind-merge"

import { ClearButton } from "./ClearButton"
import style from "./DatePicker.module.scss"
import { Icon } from "./Icon"

type AdditionalDatePickerProps = {
  containerClass?: string
  dateInputId?: string
  dateInputName?: string
  disabledDateStrings?: Array<string>
  disableWeekends?: boolean
  minDateString?: string
  onChange?: SelectSingleEventHandler
  selectedDateString?: string
}

type ReactDayPickerProps = DayPickerDefaultProps | DayPickerSingleProps | DayPickerMultipleProps | DayPickerRangeProps

export type DatePickerProps = ReactDayPickerProps & AdditionalDatePickerProps

export function DatePicker({
  containerClass,
  dateInputId,
  dateInputName = "selectedDate",
  disabledDateStrings,
  disableWeekends,
  footer,
  minDateString,
  modifiers,
  modifiersStyles,
  onChange,
  selectedDateString,
}: DatePickerProps) {
  const today = new Date()
  const selectedDate = selectedDateString ? parse(selectedDateString, "yyyy-MM-dd", today) : undefined

  const [selected, setSelected] = useState<Date | undefined>(selectedDate)
  const formattedDate = selected ? format(selected, "yyyy-MM-dd") : ""
  const content = selected ? formattedDate : "Select date"

  const classNames: ClassNames = {
    ...styles,
    caption: `${styles.caption} ${style.caption}`,
    day: `${styles.day} ${style.day}`,
    head_row: `${styles.head_row} ${style.head_row}`,
    root: `${styles.root} ${style.root}`,
    day_selected: `${styles.day_selected} ${style.day_selected}`,
    button: `${styles.button} ${style.button}`,
  }

  const disabledDateMatchers: Matcher[] = []

  // If weekends have been disabled, add a matcher to disable them
  if (disableWeekends) {
    const weekendMatcher: DayOfWeek = {
      dayOfWeek: [0, 6],
    }

    disabledDateMatchers.push(weekendMatcher)
  }

  // If there's a min date, add a matcher to disable dates before it
  if (minDateString) {
    try {
      const minDateMatcher: Matcher = {
        before: parse(minDateString, "yyyy-MM-dd", today),
      }
      disabledDateMatchers.push(minDateMatcher)
    } catch (e) {
      console.error(`Error parsing minDateString: ${e.message}`)
    }
  }

  // If there are full closures, add a matcher to disable them
  if (Array.isArray(disabledDateStrings) && disabledDateStrings.length > 0) {
    const additionalDisabledDates = disabledDateStrings.map((dateString) => parse(dateString, "yyyy-MM-dd", today))
    disabledDateMatchers.push((date: Date) => includesDay(additionalDisabledDates, date))
  }

  const onSelect = (day: Date | undefined, selectedDay: Date, activeModifiers: ActiveModifiers, e: MouseEvent) => {
    setSelected(day)
    onChange?.(day, selectedDay, activeModifiers, e)
  }

  return (
    <div className={twMerge("relative flex grow", containerClass)}>
      <Popover className="relative">
        <PopoverButton className="flex shrink-0 grow grow-1 basis-0 items-center gap-2 rounded-lg border border-gray-300 bg-white py-3 pr-10 pl-4">
          <Icon.Calendar title="Title" className="text-gray-400" />
          <span className="flex shrink-0 grow-1 flex-col justify-center self-stretch text-sm">{content}</span>
        </PopoverButton>

        <ClearButton
          onClick={() => setSelected(undefined)}
          className="absolute top-0 right-0 bottom-0 flex w-10 items-center justify-center p-0"
          title="Clear date"
        />

        <PopoverPanel anchor="bottom" className="flex flex-col">
          <DayPicker
            captionLayout="dropdown"
            classNames={classNames}
            defaultMonth={today}
            disabled={disabledDateMatchers}
            footer={footer}
            fromYear={today.getFullYear()}
            mode="single"
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            onSelect={onSelect}
            selected={selected}
            toYear={today.getFullYear() + 1}
            components={{ Day: DayWithAvailabilityDot }}
          />
        </PopoverPanel>
      </Popover>
      {(dateInputName || dateInputId) && (
        <input type="hidden" id={dateInputId} name={dateInputName} value={formattedDate} />
      )}
    </div>
  )
}

function Dot({ busy, limited }: { busy: boolean; limited: boolean }) {
  return (
    <span
      className={twMerge(
        "inline-block size-1.5 rounded-full bg-transparent",
        busy && "bg-rose-400",
        limited && "bg-yellow-300"
      )}
    />
  )
}

function DayWithAvailabilityDot(props: DayProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  // @ts-expect-error Should be fixed by upgrading react-select (BRL-7590)
  const dayRender = useDayRender(props.date, props.displayMonth, buttonRef)
  const buttonProps = dayRender.buttonProps

  const { busy, limited } = dayRender.activeModifiers

  const children = (
    <div className="tailwind-scope">
      <div className="flex gap-1">
        {(busy || limited) && <Dot busy={busy} limited={limited} />}
        {buttonProps.children}
      </div>
    </div>
  )

  if (dayRender.isHidden) {
    return <></>
  }
  if (!dayRender.isButton) {
    return <div {...dayRender.divProps} />
  }

  return (
    <Button {...dayRender.buttonProps} ref={buttonRef}>
      {children}
    </Button>
  )
}

function includesDay(dateArray: Date[], targetDate: Date): boolean {
  return dateArray.some((date) => isSameDay(targetDate, date))
}
